import { Injectable, Inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import {
  map,
  first,
  publishReplay,
  refCount,
  distinctUntilChanged,
} from 'rxjs/operators';
import { EnvironmentConfig, ENVIRONMENT_CONFIG } from '@ipreo/northstar';
import { CasaSettingsFacade } from './casa-settings.facade';
import { PermissionsFacade } from './permissions.facade';
import { DealVersionSelector } from '../types/deal.types';
import { Permissions } from '../types/user-permissions.types';
import {
  selectBuysideSharedState,
  BuysideMuniUserSetting,
  BuysideUserSettingsTypes,
  BuysideSharedModelState,
} from '../state/buyside-shared/selectors';
import {
  DEFAULT_FLEX_COLUMN,
  DEFAULT_MARGINAL_TAX_RATE,
  BenchmarkCurveIdentifier,
  UserPreferences,
  DEFAULT_CALENDAR_FOLLOW_ASSIGNMENT_SHORTCUT,
  DEFAULT_DEAL_MANAGER_VIEW,
  DEFAULT_CALENDAR_RATINGS_VIEW,
} from '../types/user-preferences.types';
import { PlatformType } from '../types/muni-environment';

@Injectable()
export class PreferencesFacade {
  public readonly preferences$: Observable<UserPreferences>;

  private platform: PlatformType;

  constructor(
    store: Store<unknown>,
    private casaSettingsFacade: CasaSettingsFacade,
    permissionsFacade: PermissionsFacade,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.platform = config.params.platform as PlatformType;

    let basePreferences: Observable<UserPreferences>;
    if (this.platform === PlatformType.buyside) {
      basePreferences = store.pipe(
        select(selectBuysideSharedState),
        map((state: BuysideSharedModelState) => {
          const settings =
            (state &&
              state.muniUserSettings &&
              state.muniUserSettings.settings) ||
            [];
          return {
            defaultFlexColumn: this.getBuysideSettingOrDefault(
              settings,
              BuysideUserSettingsTypes.defaultFlexColumn,
              DEFAULT_FLEX_COLUMN
            ),
            defaultBenchmarkCurve: this.getBuysideSettingOrDefault(
              settings,
              BuysideUserSettingsTypes.defaultBenchmarkCurve,
              BenchmarkCurveIdentifier.MmdInterpolated
            ),
            defaultMarginalTaxRate: this.getBuysideSettingOrDefault(
              settings,
              BuysideUserSettingsTypes.defaultMarginalTaxRate,
              DEFAULT_MARGINAL_TAX_RATE
            ),
            defaultDealVersions: {
              competitive: DealVersionSelector.published,
              negotiated: DealVersionSelector.published,
            },
            defaultCalendarFollowShortcut: JSON.parse(
              this.getBuysideSettingOrDefault(
                settings,
                BuysideUserSettingsTypes.defaultCalendarFollowShortcut,
                DEFAULT_CALENDAR_FOLLOW_ASSIGNMENT_SHORTCUT
              )
            ),
            defaultDealManagerView: this.getBuysideSettingOrDefault(
              settings,
              BuysideUserSettingsTypes.defaultDealManagerView,
              DEFAULT_DEAL_MANAGER_VIEW
            ),
            defaultCalendarRatingsView: this.getBuysideSettingOrDefault(
              settings,
              BuysideUserSettingsTypes.defaultCalendarRatingsView,
              DEFAULT_CALENDAR_RATINGS_VIEW
            ),
          };
        })
      );
    } else {
      basePreferences = this.casaSettingsFacade.settings$.pipe(
        map((settings) => ({
          defaultFlexColumn: DEFAULT_FLEX_COLUMN,
          defaultBenchmarkCurve: BenchmarkCurveIdentifier.MmdInterpolated,
          defaultMarginalTaxRate: DEFAULT_MARGINAL_TAX_RATE,
          defaultDealVersions: settings.defaultDealVersion,
          defaultDealManagerView: DEFAULT_DEAL_MANAGER_VIEW,
          defaultCalendarRatingsView: DEFAULT_CALENDAR_RATINGS_VIEW,
        }))
      );
    }

    this.preferences$ = combineLatest(
      basePreferences,
      permissionsFacade.permissions$.pipe(first())
    ).pipe(
      map(([preferences, permissions]) => ({
        ...preferences,
        defaultBenchmarkCurve: this.defaultCurveWithPermissions(
          preferences.defaultBenchmarkCurve,
          permissions
        ),
      })),
      distinctUntilChanged(isEqual),
      publishReplay(1),
      refCount()
    );
  }

  public getPreference<T extends keyof UserPreferences>(
    preference: T
  ): Observable<UserPreferences[T]> {
    return this.preferences$.pipe(
      map((preferences) => preferences[preference])
    );
  }

  private defaultCurveWithPermissions(
    defaultCurve: BenchmarkCurveIdentifier,
    permissions: Permissions
  ): BenchmarkCurveIdentifier {
    return !permissions.mmd && defaultCurve.startsWith('MMD')
      ? BenchmarkCurveIdentifier.MacInterpolated
      : defaultCurve;
  }

  private getBuysideSettingOrDefault<
    T extends string | Record<string, unknown>
  >(
    settings: BuysideMuniUserSetting[],
    settingName: BuysideUserSettingsTypes,
    defaultValue: T
  ): T {
    const setting = settings.find((s) => s.userPreference.name === settingName);
    return setting ? (setting.userPreference.value as T) : defaultValue;
  }
}
